import { type FC } from 'react'
import { observer } from 'mobx-react-lite'
import { type ITableConfig } from 'shared/ui'
import { AudioPlayer, type IHandleTrackOrganizationActivity } from 'shared/ui/AudioPlayer'
import { type CallHistory } from 'entities/CallHistory/model/CallHistory'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { type ICallHistoryCall } from 'entities/CallHistory/api/types'
import { sentEventSentry } from 'entities/Sentry'
import styles from 'pages/conversations/pages/calls/ui/CallHistoryTable/ui/styles.module.scss'
import { type CallHistoryStore } from 'pages/conversations/pages/calls/ui/CallHistory'

type ICellAudioProps = {
  url: string
  duration: number
  isUnread: boolean
  callItem: CallHistory
  callHistoryStore: CallHistoryStore
  tableConfig?: ITableConfig
}

export const CellAudio: FC<ICellAudioProps> = observer(
  ({ url, duration, isUnread, callItem, tableConfig, callHistoryStore }) => {
    const {
      organizationStore: { isActivityLoggingEnabled, handleTrackOrganizationActivity },
    } = useEntitiesContext()

    const handlePlaying = (info: boolean) => {
      if (isUnread && info) {
        callHistoryStore.callHistoryTableStore.markAsRead(callItem)
      }
    }

    return (
      <CellAudioUi
        url={url}
        duration={duration}
        handlePlaying={handlePlaying}
        handleTrackOrganizationActivity={handleTrackOrganizationActivity}
        isHipaaCompliant={isActivityLoggingEnabled}
        record={callItem.call}
        tableConfig={tableConfig}
      />
    )
  }
)

export const CellAudioUi = ({
  url,
  duration,
  tableConfig,
  handlePlaying,
  record,
  handleTrackOrganizationActivity,
  isHipaaCompliant,
}: {
  url: ICellAudioProps['url']
  duration: number
  tableConfig?: ICellAudioProps['tableConfig']
  handlePlaying?: (value: boolean) => void
  record_id?: string | null
  handleTrackOrganizationActivity?: (data: IHandleTrackOrganizationActivity) => void
  isHipaaCompliant?: boolean
  record?: ICallHistoryCall
}) => (
  <div
    className={styles.audioWrap}
    onClick={(e) => e.stopPropagation()}
    onKeyDown={(e) => e.stopPropagation()}
    role='presentation'
  >
    <div className={styles.audio}>
      <AudioPlayer
        url={url}
        duration={duration}
        widthWave={120}
        heightWave={16}
        size={'small'}
        colorType={tableConfig?.isHover ? 'white' : 'gray'}
        isClosed
        fromCallsHistory
        isCallAudio
        handlePlaying={handlePlaying}
        record={{ id: record?.id || 0, record_id: record?.record_id || '', sid: '' }}
        handleTrackOrganizationActivity={handleTrackOrganizationActivity}
        isHipaaCompliant={isHipaaCompliant}
        onError={(error) => {
          sentEventSentry({
            event: '[Audio] CellAudio - error',
            name: 'audio:error',
            level: 'error',
            error: error,
          })
        }}
      />
    </div>
  </div>
)
